import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Heading,
  IconButton,
  ScrollArea,
  Select,
  Separator,
  Switch,
  Tabs,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import {
  IconSettings,
  IconPlus,
  IconEdit,
  IconTrash,
  IconCopy,
} from "@tabler/icons-react";
import {
  useSmsTemplates,
  createSmsTemplate,
  updateSmsTemplate,
  deleteSmsTemplate,
} from "@/services/engagement";

const STATE_OPTIONS = [
  { value: "__none__", label: "No trigger (manual only)" },
  { value: "Pending", label: "Pending" },
  { value: "Drafting", label: "Drafting" },
  { value: "Redrafting", label: "Redrafting" },
  { value: "WaitingToPost", label: "Waiting to Post" },
  { value: "Posted", label: "Posted" },
  { value: "Declined", label: "Declined" },
  { value: "Canceled", label: "Canceled" },
];

const STATE_DESCRIPTIONS: Record<string, string> = {
  Pending: "Sent when a creator is first invited",
  Drafting: "Sent when a creator accepts the offer",
  Redrafting: "Sent when a draft revision is requested",
  WaitingToPost: "Sent when a draft is approved",
  Posted: "Sent when a live post is confirmed",
  Declined: "Sent when the creator declines",
  Canceled: "Sent when you cancel the engagement",
};

const TEMPLATE_VARIABLES = [
  { key: "{{creatorName}}", desc: "Creator's name" },
  { key: "{{campaignName}}", desc: "Campaign name" },
  { key: "{{offerLink}}", desc: "Offer page URL" },
  { key: "{{platform}}", desc: "Platform (e.g. Instagram)" },
  { key: "{{price}}", desc: "Creator's rate" },
];

export default function SmsTemplateManager() {
  const { data: templates, mutate } = useSmsTemplates();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const userTemplates = (templates || []).filter((t: any) => !t.isDefault);
  const defaultTemplates = (templates || []).filter((t: any) => t.isDefault);

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Button variant="outline" size="2">
          <IconSettings size={14} />
          Manage Templates
        </Button>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="650px">
        <Flex justify="between" align="start" mb="4">
          <Flex direction="column" gap="1">
            <Dialog.Title mb="0">SMS Templates</Dialog.Title>
            <Text size="2" color="gray">
              Customize the messages sent to creators at each stage.
            </Text>
          </Flex>
          <Button size="2" onClick={() => setShowCreate(true)}>
            <IconPlus size={14} />
            New Template
          </Button>
        </Flex>

        <Tabs.Root defaultValue="custom">
          <Tabs.List size="2">
            <Tabs.Trigger value="custom">
              Your Templates
              {userTemplates.length > 0 && (
                <Badge ml="2" size="1" variant="soft">{userTemplates.length}</Badge>
              )}
            </Tabs.Trigger>
            <Tabs.Trigger value="defaults">
              System Defaults
              <Badge ml="2" size="1" variant="soft" color="gray">{defaultTemplates.length}</Badge>
            </Tabs.Trigger>
            <Tabs.Trigger value="variables">Variables</Tabs.Trigger>
          </Tabs.List>

          <Box pt="4">
            <Tabs.Content value="custom">
              {userTemplates.length === 0 ? (
                <Flex
                  direction="column"
                  align="center"
                  gap="3"
                  py="6"
                >
                  <Text size="2" color="gray">
                    No custom templates yet. Create one to override the system defaults.
                  </Text>
                  <Button variant="soft" onClick={() => setShowCreate(true)}>
                    <IconPlus size={14} />
                    Create Your First Template
                  </Button>
                </Flex>
              ) : (
                <ScrollArea style={{ maxHeight: "400px" }}>
                  <Flex direction="column" gap="3">
                    {userTemplates.map((t: any) => (
                      <TemplateCard
                        key={t.id}
                        template={t}
                        isEditing={editingId === t.id}
                        onEdit={() => setEditingId(editingId === t.id ? null : t.id)}
                        onSave={async (data) => {
                          await updateSmsTemplate(t.id, data);
                          setEditingId(null);
                          mutate();
                        }}
                        onDelete={async () => {
                          if (confirm("Delete this template?")) {
                            await deleteSmsTemplate(t.id);
                            mutate();
                          }
                        }}
                      />
                    ))}
                  </Flex>
                </ScrollArea>
              )}
            </Tabs.Content>

            <Tabs.Content value="defaults">
              <Text size="2" color="gray" mb="3" as="p">
                These are sent automatically when no custom template exists for a state.
              </Text>
              <ScrollArea style={{ maxHeight: "400px" }}>
                <Flex direction="column" gap="3">
                  {defaultTemplates.map((t: any) => (
                    <Card key={t.id} size="1">
                      <Flex justify="between" align="center" mb="1">
                        <Flex gap="2" align="center">
                          <Text size="2" weight="bold">{t.name}</Text>
                          {t.triggerState && (
                            <Badge size="1" variant="soft" color="blue">
                              {t.triggerState}
                            </Badge>
                          )}
                        </Flex>
                        <Badge
                          color={t.enabled ? "green" : "gray"}
                          size="1"
                          variant="soft"
                        >
                          {t.enabled ? "Active" : "Inactive"}
                        </Badge>
                      </Flex>
                      {t.triggerState && STATE_DESCRIPTIONS[t.triggerState] && (
                        <Text size="1" color="gray" as="p" mb="2">
                          {STATE_DESCRIPTIONS[t.triggerState]}
                        </Text>
                      )}
                      <Box
                        p="2"
                        style={{
                          borderRadius: "var(--radius-2)",
                          background: "var(--gray-a3)",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        <Text size="1">{t.body}</Text>
                      </Box>
                    </Card>
                  ))}
                </Flex>
              </ScrollArea>
            </Tabs.Content>

            <Tabs.Content value="variables">
              <Text size="2" color="gray" mb="3" as="p">
                Use these placeholders in your templates. They get replaced with real values when sending.
              </Text>
              <Flex direction="column" gap="2">
                {TEMPLATE_VARIABLES.map((v) => (
                  <Flex
                    key={v.key}
                    justify="between"
                    align="center"
                    p="2"
                    style={{
                      borderRadius: "var(--radius-2)",
                      background: "var(--gray-a3)",
                    }}
                  >
                    <Flex gap="3" align="center">
                      <code
                        style={{
                          fontFamily: "var(--code-font-family)",
                          fontSize: "var(--font-size-2)",
                          padding: "2px 6px",
                          borderRadius: "var(--radius-1)",
                          background: "var(--accent-a3)",
                          color: "var(--accent-11)",
                        }}
                      >
                        {v.key}
                      </code>
                      <Text size="2" color="gray">{v.desc}</Text>
                    </Flex>
                    <Tooltip content="Copy to clipboard">
                      <IconButton
                        size="1"
                        variant="ghost"
                        onClick={() => navigator.clipboard.writeText(v.key)}
                      >
                        <IconCopy size={14} />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                ))}
              </Flex>
            </Tabs.Content>
          </Box>
        </Tabs.Root>

        <Separator my="3" size="4" />

        <Flex justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Done</Button>
          </Dialog.Close>
        </Flex>

        {showCreate && (
          <CreateTemplateDialog
            open={showCreate}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); mutate(); }}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function TemplateCard({
  template,
  isEditing,
  onEdit,
  onSave,
  onDelete,
}: {
  template: any;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (data: any) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [name, setName] = useState(template.name);
  const [body, setBody] = useState(template.body);
  const [triggerState, setTriggerState] = useState(template.triggerState || "__none__");
  const [enabled, setEnabled] = useState(template.enabled);

  if (!isEditing) {
    return (
      <Card size="1">
        <Flex justify="between" align="center" mb="1">
          <Flex gap="2" align="center">
            <Text size="2" weight="bold">{template.name}</Text>
            {template.triggerState && (
              <Badge size="1" variant="soft" color="blue">
                {template.triggerState}
              </Badge>
            )}
            <Badge
              color={template.enabled ? "green" : "gray"}
              size="1"
              variant="soft"
            >
              {template.enabled ? "Active" : "Inactive"}
            </Badge>
          </Flex>
          <Flex gap="1">
            <IconButton size="1" variant="ghost" title="Edit" onClick={onEdit}>
              <IconEdit size={14} />
            </IconButton>
            <IconButton size="1" variant="ghost" color="red" title="Delete" onClick={onDelete}>
              <IconTrash size={14} />
            </IconButton>
          </Flex>
        </Flex>
        <Box
          p="2"
          style={{
            borderRadius: "var(--radius-2)",
            background: "var(--gray-a3)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <Text size="1">{template.body}</Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card size="1" style={{ border: "2px solid var(--accent-7)" }}>
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Text size="1" weight="medium" color="gray">Template Name</Text>
          <TextField.Root value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Friendly Welcome" />
        </Flex>
        <Flex direction="column" gap="1">
          <Text size="1" weight="medium" color="gray">Auto-send on State</Text>
          <Select.Root value={triggerState} onValueChange={setTriggerState}>
            <Select.Trigger />
            <Select.Content>
              {STATE_OPTIONS.map((o) => (
                <Select.Item key={o.value} value={o.value}>{o.label}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
        <Flex direction="column" gap="1">
          <Text size="1" weight="medium" color="gray">Message Body</Text>
          <TextArea value={body} onChange={(e) => setBody(e.target.value)} rows={4} />
        </Flex>
        <Separator size="4" />
        <Flex justify="between" align="center">
          <Flex gap="2" align="center">
            <Switch checked={enabled} onCheckedChange={setEnabled} size="1" />
            <Text size="2">{enabled ? "Active" : "Inactive"}</Text>
          </Flex>
          <Flex gap="2">
            <Button size="1" variant="soft" color="gray" onClick={onEdit}>Cancel</Button>
            <Button
              size="1"
              onClick={() => onSave({
                name,
                body,
                triggerState: triggerState === "__none__" ? null : triggerState,
                enabled,
              })}
              disabled={!name.trim() || !body.trim()}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}

function CreateTemplateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [triggerState, setTriggerState] = useState("__none__");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await createSmsTemplate({
        name,
        body,
        triggerState: triggerState === "__none__" ? undefined : triggerState,
      });
      onCreated();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>New Template</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="3">
          Create a custom message template. Assign it to a state to auto-send, or leave it manual.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text size="1" weight="medium" color="gray">Template Name</Text>
            <TextField.Root
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Friendly Reminder"
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text size="1" weight="medium" color="gray">Auto-send on State</Text>
            <Select.Root value={triggerState} onValueChange={setTriggerState}>
              <Select.Trigger />
              <Select.Content>
                {STATE_OPTIONS.map((o) => (
                  <Select.Item key={o.value} value={o.value}>{o.label}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
          <Flex direction="column" gap="1">
            <Flex justify="between" align="center">
              <Text size="1" weight="medium" color="gray">Message Body</Text>
              <Text size="1" color="gray">
                Tip: use {"{{creatorName}}"}, {"{{campaignName}}"}, etc.
              </Text>
            </Flex>
            <TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{creatorName}}! We'd love to work with you on {{campaignName}}..."
              rows={5}
            />
          </Flex>

          <Separator size="4" />

          <Flex gap="3" justify="end">
            <Button variant="soft" color="gray" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !name.trim() || !body.trim()}
            >
              {saving ? "Creating..." : "Create Template"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
